import { nanoid } from 'nanoid/non-secure';
import { createElement } from 'react';

import type { ModalKey, ModalPayloadMap, ModalResultMap } from '../modals';

import { useModalStore } from '../../stores/useModalStore';

type ModalKeyWithoutPayload = {
  [K in ModalKey]: undefined extends ModalPayloadMap[K] ? K : never;
}[ModalKey];

const openWithPayload = <K extends ModalKey>(action: K, payload: ModalPayloadMap[K]) => {
  const { push, remove } = useModalStore.getState();
  const id = nanoid();

  return new Promise<ModalResultMap[K] | undefined>((resolve) => {
    const settle = (result: ModalResultMap[K] | undefined) => {
      resolve(result);
      remove(id);
    };

    push({
      id,
      action,
      render: (definitions) =>
        createElement(definitions[action].component, {
          payload,
          api: { close: settle, cancel: () => settle(undefined) },
        }),
      dismiss: () => settle(undefined),
    });
  });
};

function openModal<K extends ModalKeyWithoutPayload>(action: K): Promise<ModalResultMap[K] | undefined>;
function openModal<K extends ModalKey>(action: K, payload: ModalPayloadMap[K]): Promise<ModalResultMap[K] | undefined>;
function openModal(action: ModalKey, payload?: ModalPayloadMap[ModalKey]) {
  return openWithPayload(action, payload);
}

export function useModal() {
  return { open: openModal };
}
